// --- CSV Scanner saved-scan storage ---
// Scan metadata lives in localStorage ("nepetoCsvScans", small).
// Scan products live in IndexedDB, keyed by scan id (can be tens of MB).

const DB_NAME = "nepetoScans";
const DB_VERSION = 1;
const STORE = "scans";
const META_KEY = "nepetoCsvScans";
const MAX_SAVED_SCANS = 10;

const isSupported = () => typeof window !== "undefined" && !!window.indexedDB;

const openDB = () =>
	new Promise((resolve, reject) => {
		if (!isSupported()) {
			reject(new Error("IndexedDB unavailable"));
			return;
		}
		const request = window.indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE)) {
				db.createObjectStore(STORE, { keyPath: "id" });
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
		request.onblocked = () => reject(new Error("IndexedDB blocked"));
	});

const runTx = (mode, action) =>
	openDB().then(
		(db) =>
			new Promise((resolve, reject) => {
				const tx = db.transaction(STORE, mode);
				const store = tx.objectStore(STORE);
				let result;
				try {
					result = action(store);
				} catch (e) {
					db.close();
					reject(e);
					return;
				}
				tx.oncomplete = () => {
					db.close();
					resolve(result instanceof IDBRequest ? result.result : result);
				};
				tx.onerror = () => {
					db.close();
					reject(tx.error);
				};
				tx.onabort = () => {
					db.close();
					reject(tx.error || new Error("Transaction aborted"));
				};
			})
	);

// Ask the browser not to evict us under storage pressure. Best effort.
export const requestScanPersistence = () => {
	try {
		if (navigator.storage && navigator.storage.persist) {
			navigator.storage.persisted().then((already) => {
				if (!already) navigator.storage.persist().catch(() => {});
			}).catch(() => {});
		}
	} catch (e) {
		// Not supported — scans still save, they are just evictable.
	}
};

// --- Metadata (localStorage) ---

export const getScanHistory = () => {
	try {
		return JSON.parse(window.localStorage.getItem(META_KEY)) || [];
	} catch (e) {
		return [];
	}
};

const writeScanHistory = (history) => {
	try {
		window.localStorage.setItem(META_KEY, JSON.stringify(history));
	} catch (e) {
		// Quota or private mode — metadata is cosmetic, fail silently.
	}
};

export const addScanToHistory = (entry) => {
	const history = getScanHistory();
	history.push(entry);
	writeScanHistory(history);
	return history;
};

export const updateScanInHistory = (id, changes) => {
	const history = getScanHistory().map((item) =>
		item.id === id ? { ...item, ...changes } : item
	);
	writeScanHistory(history);
	return history;
};

// --- Products (IndexedDB) ---

export const saveScanProducts = (id, products) =>
	runTx("readwrite", (store) => store.put({ id, products })).catch(() => false);

export const loadScanProducts = (id) =>
	runTx("readonly", (store) => store.get(id))
		.then((record) => (record && record.products ? record.products : null))
		.catch(() => null);

export const deleteScanProducts = (id) =>
	runTx("readwrite", (store) => store.delete(id)).catch(() => false);

export const deleteScan = (id) => {
	const history = getScanHistory().filter((item) => item.id !== id);
	writeScanHistory(history);
	return deleteScanProducts(id).then(() => history);
};

// Drop the oldest saved scans so storage does not grow forever.
export const pruneScans = (keep = MAX_SAVED_SCANS) => {
	const history = getScanHistory();
	const saved = history.filter((item) => item.id);
	if (saved.length <= keep) return Promise.resolve(history);

	const stale = saved.slice(0, saved.length - keep);
	const staleIds = stale.map((item) => item.id);
	const remaining = history.filter((item) => !staleIds.includes(item.id));
	writeScanHistory(remaining);

	return Promise.all(staleIds.map((staleId) => deleteScanProducts(staleId))).then(
		() => remaining
	);
};

export const makeScanId = () =>
	`scan_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
